import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero.jsx';
import TileGrid from '../components/TileGrid.jsx';
import { church } from '../data/church.js';
import { api } from '../lib/api.js';
import usePageMeta from '../lib/usePageMeta.js';
import './Home.css';

function formatDayParts(iso) {
  const d = new Date(iso);
  return {
    month: d.toLocaleDateString('en-US', { month: 'short' }),
    day: d.toLocaleDateString('en-US', { day: 'numeric' }),
    full: d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
}

/** Pulls the next few published events. Fails quietly — the rest of the
 *  home page must not depend on the API being up. */
function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [state, setState] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();

    api
      .get('/events/upcoming?limit=4', { auth: false, signal: controller.signal })
      .then((data) => {
        setEvents(data.events || []);
        setState('ready');
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setState('error');
      });

    return () => controller.abort();
  }, []);

  if (state === 'loading') {
    return (
      <p className="type-body" role="status">
        Loading upcoming events…
      </p>
    );
  }

  if (state === 'error' || events.length === 0) {
    return (
      <div className="alert alert--info">
        <p>
          Our calendar is being updated. Call the church office at{' '}
          <a href={church.phoneHref}>{church.phone}</a> for this week&rsquo;s schedule.
        </p>
      </div>
    );
  }

  return (
    <ul className="upcoming">
      {events.map((event) => {
        const parts = formatDayParts(event.startsAt);
        return (
          <li className="upcoming__item" key={event.id}>
            <div className="upcoming__date" aria-hidden="true">
              <span className="upcoming__month">{parts.month}</span>
              <span className="upcoming__day">{parts.day}</span>
            </div>
            <div>
              <h3 className="upcoming__title">{event.title}</h3>
              <p className="upcoming__meta">
                {/* Visible date is abbreviated; this carries the full one. */}
                <span className="sr-only">{parts.full}, </span>
                {event.allDay ? 'All day' : parts.time}
                {event.location ? ` · ${event.location}` : ''}
              </p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export default function Home() {
  usePageMeta(
    null,
    'Metropolitan Church of God in Detroit, Michigan. Sunday School 8:30 a.m., Sunday Worship 9:45 a.m. All are welcome.'
  );

  return (
    <>
      <Hero />

      {/* --- Four tiles --- */}
      <section className="section section--tight" aria-labelledby="explore-heading">
        <div className="container">
          <h2 id="explore-heading" className="sr-only">
            Explore Metropolitan
          </h2>
          <TileGrid />
        </div>
      </section>

      {/* --- Welcome / mission --- */}
      <section className="section section--subtle" aria-labelledby="welcome-heading">
        <div className="container home-intro">
          <div>
            <p className="section-eyebrow">Our mission</p>
            <h2 className="type-h2 home-intro__title" id="welcome-heading">
              A community committed to God, to fellowship, and to service.
            </h2>
            <p className="home-intro__body">
              Metropolitan Church of God is called to be a healthy community of
              believers, establishing the Lordship of Christ in the hearts of men
              and women while demonstrating God&rsquo;s love through relief,
              development, and structural transformation across Metropolitan
              Detroit.
            </p>
            <div className="cluster mt-6">
              <Link to="/about/vision-mission" className="btn btn--primary">
                Our Vision &amp; Mission
              </Link>
              <Link to="/about/what-we-believe" className="btn btn--secondary">
                What We Believe
              </Link>
            </div>
          </div>

          <div className="home-intro__media">
            <img
              src="/assets/mcog-fellowship-2.jpg"
              alt="Members of Metropolitan Church of God gathered together in fellowship."
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* --- Service times --- */}
      <section className="section section--dark on-dark" aria-labelledby="times-heading">
        <div className="container">
          <p className="section-eyebrow">Join us</p>
          <h2 className="type-h2 mt-6" id="times-heading" style={{ marginTop: 'var(--space-3)' }}>
            Weekly gatherings
          </h2>

          <div className="service-times mt-7">
            <div className="service-card">
              <p className="service-card__label">Sunday School</p>
              <p className="service-card__time">8:30 AM</p>
              <p className="service-card__note">
                Classes for every age, beginning an hour before worship.
              </p>
            </div>

            <div className="service-card">
              <p className="service-card__label">Sunday Worship</p>
              <p className="service-card__time">9:45 AM</p>
              <p className="service-card__note">
                Our main worship service, ending around 11:00 a.m. Children are
                dismissed to Committed Kids about thirty minutes in.
              </p>
            </div>

            <div className="service-card">
              <p className="service-card__label">Bible Study</p>
              <p className="service-card__time">Various</p>
              <p className="service-card__note">
                The Education Institute meets on Tuesdays, Wednesdays, and
                Saturdays, in person and on Zoom.
              </p>
            </div>
          </div>

          <div className="cluster mt-7">
            <Link to="/visiting/location-service-times" className="btn btn--gold">
              Plan Your Visit
            </Link>
            <Link to="/news-events/calendar" className="btn btn--secondary">
              See the Full Calendar
            </Link>
          </div>
        </div>
      </section>

      {/* --- Upcoming --- */}
      <section className="section" aria-labelledby="upcoming-heading">
        <div className="container">
          <p className="section-eyebrow">This week</p>
          <h2
            className="type-h2"
            id="upcoming-heading"
            style={{ marginTop: 'var(--space-3)', marginBottom: 'var(--space-6)' }}
          >
            What&rsquo;s coming up
          </h2>

          <UpcomingEvents />

          <p className="mt-6">
            <Link to="/news-events/calendar" className="btn btn--ghost">
              View the full calendar
              <svg className="arrow" width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
                <path
                  d="M1 5h11M8.5 1L12.5 5L8.5 9"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
          </p>
        </div>
      </section>

      {/* --- Closing CTA --- */}
      <section className="section section--dark on-dark">
        <div className="container cta-band">
          <h2 className="cta-band__title">
            Ready to take
            <br />
            the next step?
          </h2>
          <p className="cta-band__body">
            Whether you are visiting for the first time, looking for a Bible
            study, or wanting to serve your neighbors in Detroit, we would love
            to hear from you.
          </p>
          <div className="cta-band__actions">
            <Link to="/contact" className="btn btn--gold">
              Connect With Us
            </Link>
            <Link to="/give" className="btn btn--secondary">
              Give
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
